import { ChatType } from "./chatTypes";
import { CHAT_THEME, CHAT_CONTENT } from "../constants/chatTheme";

export interface ChatInboxConfig {
  chatType: ChatType;
  position: "left" | "right";

  styles: {
    bgColor: string;
    hoverBgColor: string;
    borderColor: string;
    accentColor: string;
    headerBg: string;
    headerBgUnread: string;
    headerColor: string;
    headerColorUnread: string;
    headerHover: string;
    headerHoverUnread: string;
    borderBottomColor: string;
    searchBg: string;
    searchBorder: string;
    searchFocusBorder: string;
    drawerBg: string;
    drawerBorder: string;
    unreadBadgeBg: string;
    unreadBadgeColor: string;
  };

  content: {
    title: string;
    chatTypeIcon: string;
    chatTypeLabel: string;
    searchPlaceholder: string;
    emptyMessage: string;
  };

  behavior: {
    onChatClick: (uniqueName: string, conversation?: any) => Promise<void>;
    getParticipantName: (chat: any) => Promise<string>;
    getParticipantId: (chat: any) => string;
  };
}

export interface ChatBoxConfig {
  chatType: ChatType;

  styles: {
    headerBg: string;
    headerBorder: string;
    messageBg: string;
    messageBorder: string;
    accentColor: string;
    accentColorHover: string;
    sendButtonColor: string;
    inputFocusColor: string;
    unreadBadgeBg: string;
    unreadBadgeColor: string;
  };

  content: {
    chatTypeLabel: string;
    chatTypeIcon: string;
  };

  positioning: {
    getPosition: () => string;
    getSide: () => "left" | "right";
  };
}

const READ_THEME = {
  bgColor: "blue.50",
  hoverBgColor: "blue.100",
  borderColor: "blue.200",
  accentColor: "blue.400",
  headerBg: "blue.300",
  headerColor: "white",
  headerHover: "blue.400",
  searchBg: "blue.50",
  searchBorder: "blue.200",
  searchFocusBorder: "blue.400",
  drawerBg: "blue.50",
  drawerBorder: "blue.200",
};

const UNREAD_THEME = {
  headerBgUnread: "green.400",
  headerColorUnread: "white",
  headerHoverUnread: "green.500",
  unreadBadgeBg: "red.400",
  unreadBadgeColor: "white",
};

export const CHAT_INBOX_CONFIGS: Record<
  ChatType,
  Omit<ChatInboxConfig, "chatType" | "position" | "behavior">
> = {
  [ChatType.CUSTOMER]: {
    styles: {
      ...READ_THEME,
      ...UNREAD_THEME,
      borderBottomColor: READ_THEME.borderColor,
    },
    content: {
      title: CHAT_CONTENT.CUSTOMER.TITLE,
      chatTypeIcon: CHAT_CONTENT.CUSTOMER.ICON,
      chatTypeLabel: CHAT_CONTENT.CUSTOMER.LABEL,
      searchPlaceholder: CHAT_CONTENT.CUSTOMER.SEARCH_PLACEHOLDER,
      emptyMessage: CHAT_CONTENT.CUSTOMER.EMPTY_MESSAGE,
    },
  },
  [ChatType.SUPPLIER]: {
    styles: {
      ...READ_THEME,
      ...UNREAD_THEME,
      borderBottomColor: READ_THEME.borderColor,
    },
    content: {
      title: CHAT_CONTENT.SUPPLIER.TITLE,
      chatTypeIcon: CHAT_CONTENT.SUPPLIER.ICON,
      chatTypeLabel: CHAT_CONTENT.SUPPLIER.LABEL,
      searchPlaceholder: CHAT_CONTENT.SUPPLIER.SEARCH_PLACEHOLDER,
      emptyMessage: CHAT_CONTENT.SUPPLIER.EMPTY_MESSAGE,
    },
  },
};

export const CHAT_BOX_CONFIGS: Record<
  ChatType,
  Omit<ChatBoxConfig, "chatType" | "positioning">
> = {
  [ChatType.CUSTOMER]: {
    styles: {
      headerBg: READ_THEME.headerBg,
      headerBorder: READ_THEME.borderColor,
      messageBg: READ_THEME.bgColor,
      messageBorder: READ_THEME.borderColor,
      accentColor: READ_THEME.accentColor,
      accentColorHover: "blue.600",
      sendButtonColor: "blue",
      inputFocusColor: READ_THEME.accentColor,
      unreadBadgeBg: UNREAD_THEME.unreadBadgeBg,
      unreadBadgeColor: UNREAD_THEME.unreadBadgeColor,
    },
    content: {
      chatTypeLabel: CHAT_CONTENT.CUSTOMER.LABEL,
      chatTypeIcon: CHAT_CONTENT.CUSTOMER.ICON,
    },
  },
  [ChatType.SUPPLIER]: {
    styles: {
      headerBg: READ_THEME.headerBg,
      headerBorder: READ_THEME.borderColor,
      messageBg: READ_THEME.bgColor,
      messageBorder: READ_THEME.borderColor,
      accentColor: READ_THEME.accentColor,
      accentColorHover: "blue.600",
      sendButtonColor: "blue",
      inputFocusColor: READ_THEME.accentColor,
      unreadBadgeBg: UNREAD_THEME.unreadBadgeBg,
      unreadBadgeColor: UNREAD_THEME.unreadBadgeColor,
    },
    content: {
      chatTypeLabel: CHAT_CONTENT.SUPPLIER.LABEL,
      chatTypeIcon: CHAT_CONTENT.SUPPLIER.ICON,
    },
  },
};
